import TableTools from "@/utils/tableTools";
import mesureEnum from "@/model/mesureEnum";
import dimensionEnum from "@/model/dimensionEnum";
import {
  columnTypeEnum,
  calculatedColumnsEnum,
  alternativeColumnLabelEnum,
} from "@/model/columnsEnums";
import i18n from "@/plugins/vue-i18n.js";
import { gamesHelper } from "@/utils/gamesHelper";

export default {
  namespaced: true,
  state: {
    dimensionColumnsTables: [],
    measureColumnsTables: [],
    groupHeaders: [],
    defaultSortedColumnTables: "",
    tablesData: [],
    hideGroupsByTableNumber: {},
    hideCustomGroupsByTableNumber: {},
    dataReadyTables: false,
  },
  mutations: {
    setDimensionColumnsTables(state, data) {
      state.dimensionColumnsTables = data;
    },
    setMeasureColumnsTables(state, data) {
      state.measureColumnsTables = data;
    },
    setGroupHeaders(state, data) {
      state.groupHeaders = data;
    },
    setDefaultSortedColumnTables(state, data) {
      state.defaultSortedColumnTables = data;
    },
    setTablesData(state, data) {
      state.tablesData = data;
    },
    updateTableData(state, payload) {
      state.tablesData = state.tablesData.map((tab) => {
        if (tab.tableHeaderId === payload.siteId) {
          return {
            ...tab,
            dataTable: payload.dataTable,
            totalsData: payload.totalsData,
          };
        }
        return tab;
      });
    },
    setHideGroupsByTableNumber(state, data) {
      state.hideGroupsByTableNumber = data;
    },
    setHideCustomGroupsByTableNumber(state, data) {
      state.hideCustomGroupsByTableNumber = data;
    },
    updateHideGroupsByTableNumber(state, payload) {
      state.hideGroupsByTableNumber[payload.tableNumber] = payload.value;
    },
    updateHideCustomGroupsByTableNumber(state, payload) {
      state.hideCustomGroupsByTableNumber[payload.tableNumber] = payload.value;
    },
    setDataReadyTables(state, data) {
      state.dataReadyTables = data;
    },
  },
  actions: {
    resetDataReadyTables({ commit }) {
      commit("setDataReadyTables", false);
    },
    initDisplayTables({ commit }, payload) {
      const siteCurrency = payload.siteCurrency || 'EUR';
      let dimensionColumnsTables = TableTools.Helper.constructColumnTable(
        [
          calculatedColumnsEnum.RANK,
          dimensionEnum.GAMECATEGORY_NAME,
          dimensionEnum.GAME_NAME,
          dimensionEnum.SMARTTOUCH_NUMBER,
          dimensionEnum.GAME_CODE,
        ],
        null,
        columnTypeEnum.DIMENSION,
        true,
        null
      );
      dimensionColumnsTables = dimensionColumnsTables.map((col) => {
        if (col.key == dimensionEnum.GAME_NAME) {
          col.tdAttr = (value, key) => {
            return {
              title: TableTools.Helper.valueFormatter(value, key),
            };
          };
        }
        return col;
      });
      commit("setDimensionColumnsTables", dimensionColumnsTables);

      commit(
        "setMeasureColumnsTables",
        TableTools.Helper.constructColumnTable(
          [
            dimensionEnum.GAME_TOKENPRICEASDIMENSION,
            mesureEnum.GAMEHISTORY_TOKEN_PRICE,
            mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
            dimensionEnum.GAMEHISTORY_TAXESPERCENT,
            calculatedColumnsEnum.PERCENT_REVENUE_PER_GAME,
            mesureEnum.GAMEHISTORY_COUNT,
            alternativeColumnLabelEnum.GAMEHISTORY_TOKEN,
            mesureEnum.GAMEHISTORY_BONUSTOKEN,
            calculatedColumnsEnum.PERCENT_BONUS_TOKEN,
            mesureEnum.STAFFGAMEHISTORY_COUNT,
          ],
          null,
          columnTypeEnum.MEASURE,
          true,
          null,
          false,
          siteCurrency
        )
      );

      commit("setGroupHeaders", [
        { nbOfMeasures: 1, label: i18n.t("group.unitPrice") },
        { nbOfMeasures: 3, label: i18n.t("group.turnoverEquivalent") },
        { nbOfMeasures: 5, label: i18n.t("group.consumedTokens") },
        { nbOfMeasures: 1, label: i18n.t("group.staff") },
      ]);

      commit("setDefaultSortedColumnTables", payload.defaultSortedColumnTables);
    },
    async addGameGroupsRows(_,
      {
        dataTable,
        displayTaxePercent,
        groupColumnsTables,
        isGroupedByCode,
        isGamesCodeView = false
      }){
        
      return TableTools.Helper.addGroupRows(
        dataTable,
        [
          isGroupedByCode 
            ? dimensionEnum.GAMECATEGORY_NAME 
            : groupColumnsTables
        ],
        [
          mesureEnum.GAMEHISTORY_TOKEN_PRICE,
          mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
          mesureEnum.GAMEHISTORY_COUNT,
          alternativeColumnLabelEnum.GAMEHISTORY_TOKEN,
          mesureEnum.GAMEHISTORY_BONUSTOKEN,
          mesureEnum.STAFFGAMEHISTORY_COUNT,
        ],
        [
          dimensionEnum.GAMECATEGORY_NAME,
          ...(!isGroupedByCode && isGamesCodeView
            ? [dimensionEnum.GAME_NAME, dimensionEnum.GAME_TOKENPRICEASDIMENSION]
            : []),
          ...(displayTaxePercent
            ? [dimensionEnum.GAMEHISTORY_TAXESPERCENT]
            : [])
        ],
        [dimensionEnum.SMARTTOUCH_NUMBER],
        isGroupedByCode
      );
    },
    async updateTableList({ commit, dispatch }, payload) {
      // Initialisation des objets contenant toutes les données pour chaque site :
      // permet d'afficher dès maintenant les entêtes de tableaux et le loader pour indiquer que les requêtes sont en cours
      const tablesData = payload.siteValues.map((site, i) => {
        return {
          tableNumber: i.toString(),
          tableHeader: site.label,
          tableHeaderId: site.id,
          dataTable: [],
          totalsData: {},
        };
      });
      // Initialisation des lignes de catégories à cacher par défaut
      let hideGroupsByTableNumber = {};
      tablesData.forEach((table) => {
        hideGroupsByTableNumber[
          table.tableNumber
        ] = !payload.isDefaultDisplayCategoriesRows;
      });
      commit("setHideGroupsByTableNumber", hideGroupsByTableNumber);

      // Initialisation des lignes de groupement custom dimension.GAMECATEGORY_NAME
      // seulement pour la vue GamesCode
      // On est obligé de créer des groupements custom étant donné que le tableau de ce rapport
      // n'affiche en réalité que des lignes de groupement groupLevel == 0
      if(payload.isGroupedByCode){
        let hideGroupsCustomByTableNumber = {};
        tablesData.forEach((table) => {
          hideGroupsCustomByTableNumber[
            table.tableNumber
          ] = true;
        });

        commit("setHideCustomGroupsByTableNumber", hideGroupsCustomByTableNumber);
      }
      commit("setTablesData", tablesData);

      const queries = gamesHelper.getQueries(payload.filters, payload.timeDimension);

      const resultSets = await TableTools.Helper.doMultipleRequest(queries);
      let mergedResults = TableTools.Helper.mergeResultsByCommonDimensions(
        resultSets,
        gamesHelper.dimensions
      );

      // Nettoyage des anomalies
      mergedResults = gamesHelper.sortAnomalies(mergedResults);

      // Extraire la devise du premier résultat
      const siteCurrency = mergedResults.length > 0 ? (mergedResults[0][dimensionEnum.SITE_CURRENCY] || 'EUR') : 'EUR';
      await dispatch('initDisplayTables', {
        defaultSortedColumnTables: payload.defaultSortedColumnTables,
        siteCurrency: siteCurrency
      });


      const queryStaffGameHistory = gamesHelper.getStaffNumberGamesQueries(payload.filters, payload.timeDimension)

      const resultSetsStaffGameHistory = await TableTools.Helper.doRequest(queryStaffGameHistory)
      // modifier les résultats pour ajouter le nombre de parties des Staff par SmartTouchNumber
      // on ne le fait pas par query car avec le merge le nombre devient faux
      mergedResults = mergedResults.map((result) => {
        const count = resultSetsStaffGameHistory.filter(staffGameHistory => {
          return (
            staffGameHistory[dimensionEnum.SMARTTOUCH_NUMBER] === result[dimensionEnum.SMARTTOUCH_NUMBER]
          );
        }).length;

        return {
          ...result,
          [mesureEnum.STAFFGAMEHISTORY_COUNT]: count,
        }
      })

      // Complétion des objets avec les données reçues pour chaque site
      payload.siteValues.forEach(async (site) => {
        let dataTable = mergedResults.filter(
          (result) => result[dimensionEnum.SITE] === site.id
        );

        // Extraire la devise spécifique à ce site
        const siteCurrency = dataTable.length > 0 ? (dataTable[0][dimensionEnum.SITE_CURRENCY] || 'EUR') : 'EUR';

        // Ajouter la devise à chaque ligne pour le formatage
        dataTable.forEach(row => {
          row["Site.currency"] = siteCurrency;
        });

        // Rename column for alternative label
        dataTable.forEach((data) =>
          TableTools.Helper.renameKey(
            data,
            mesureEnum.GAMEHISTORY_TOKEN,
            alternativeColumnLabelEnum.GAMEHISTORY_TOKEN
          )
        );

        const totalsData = TableTools.Helper.calculateTotalsData(dataTable, [
          mesureEnum.GAMEHISTORY_TOKEN_PRICE,
          mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
          mesureEnum.GAMEHISTORY_COUNT,
          alternativeColumnLabelEnum.GAMEHISTORY_TOKEN,
          mesureEnum.GAMEHISTORY_BONUSTOKEN,
          mesureEnum.STAFFGAMEHISTORY_COUNT,
        ]);

        // Ajout des lignes de groupement
        if (payload.withCategoriesRows) {
          dataTable = await dispatch("addGameGroupsRows", {
            dataTable : dataTable,
            isGroupedByCode : false,
            groupColumnsTables : payload.groupColumnsTables,
            isGamesCodeView: payload.isGroupedByCode,
            displayTaxePercent: payload.displayTaxePercent
          })

          // ajout des lignes de groupement dimension.GAMECATEGORY_NAME pour la vue GamesCode
          // on est obligé de faire comme ça car la vue GamesCode est déjà groupé par dimensionEnum.GAME_CODE
          if(payload.isGroupedByCode){
              dataTable = await dispatch("addGameGroupsRows", {
              dataTable : dataTable,
              isGroupedByCode : true,
              isGamesCodeView: true
            })
          }

          // Ajout de la devise aux lignes
          dataTable.forEach(row => {
            row["Site.currency"] = siteCurrency;
          });
        }

        dataTable = gamesHelper.addCalculatedColumns(
          dataTable,
          totalsData[mesureEnum.GAMEHISTORY_TOKEN_PRICE],
          payload.isGroupedByCode
        );

        // On ajoute la ligne de calcul % unité bonus
        const bonusToken = totalsData[mesureEnum.GAMEHISTORY_BONUSTOKEN] || 0;
        const token = totalsData[alternativeColumnLabelEnum.GAMEHISTORY_TOKEN] || 0;
        const totalTokens = bonusToken + token;
        totalsData[calculatedColumnsEnum.PERCENT_BONUS_TOKEN] =
          totalTokens !== 0
            ? (bonusToken * 100) / totalTokens
            : 0;


        // On retire les groupLevel si on est dans la view GameCode
        // pour avoir un affichage des lignes groupées sans couleurs $primary
        if (payload.isGroupedByCode) {
          dataTable.forEach(data => {
            // on enlève groupLevel seulement pour les lignes de groupement GamesCode
            // étant donné que le tableau est composé uniquement de ligne de groupement
            // cela permet de ne pas afficher la couleur bleu par défaut de ces lignes
            // pour les lignes de groupement GameCategory, on les laisse
            if(!data.customGroup){
              delete data.groupLevel;
            }
          });
        }

        commit("updateTableData", { siteId: site.id, dataTable, totalsData });
      });
      commit("setDataReadyTables", true);
    },
    updateHideGroupsByTableNumber({ commit }, payload) {
      commit("updateHideGroupsByTableNumber", payload);
    },
    updateHideCustomGroupsByTableNumber({ commit }, payload) {
      commit("updateHideCustomGroupsByTableNumber", payload);
    },
  },
};
